import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Col, InputNumber, Row } from 'antd'
import '../../css/schedule.scss'
import {
  cardStyle,
  sectionHeaderRow,
  sectionTitle,
  fieldLabelStyle,
  overriddenDot,
} from './tracpatch.config.styles'
import {
  WEEK_ROW_FIELDS,
  clinicOrTieredDefault,
  resolveWeekCell,
} from './tracpatch.week.defaults'

const tableHeaderStyle = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: '#64748b',
  whiteSpace: 'nowrap',
}

// User-facing labels for the internal angle field names. Used in the
// monotonicity error messages so clinicians see the same wording they
// see on the table headers. Canonical chain (numeric ascending):
//   ext_limit ≤ ext_high ≤ ext_low ≤ flex_low ≤ flex_high ≤ flex_limit
// Naming convention on the dial:
//   ext_high = the smaller-numerical / more extended gate ("Higher Ext")
//   ext_low  = the larger-numerical / less extended gate  ("Lower Ext")
const ANGLE_FIELD_LABELS = {
  extension_limit: 'Extension Limit',
  extension_high:  'Higher Extension',
  extension_low:   'Lower Extension',
  flexion_low:     'Lower Flexion',
  flexion_high:    'Higher Flexion',
  flexion_limit:   'Flexion Limit',
}

/**
 * Per-patient override form for TracPatch exercise defaults.
 *
 * Renders 4 targets, restAngle, and a
 * per-week REP MILESTONE editor. Each scalar field is pre-filled with the
 * resolved value: `overrides[field] ?? masterConfig[field]`.
 *
 * Props:
 *   masterConfig            — the resolved defaults (Exercise.tracpatchConfig)
 *   overrides               — current per-patient overrides on the schedule entry
 *   onChange(nextOverrides) — called with the next overrides object when user edits
 *   edit                    — boolean; when true, inputs are editable
 *   numWeeks                — number of post-op week rows to show in REP MILESTONE
 *                              (derived from schedule entry's enddate by the modal)
 */
const TracPatchOverrideForm = (props) => {
  const masterConfig = props.masterConfig || {}
  const overrides = props.overrides || {}
  const edit = !!props.edit
  const disabled = !edit
  const numWeeks = Math.max(1, props.numWeeks || 6)
  // Reps come FROM the care plan (decision 2026-10-16): when the caller passes
  // `repsByWeek` ({ [week]: reps | null }), the Reps column shows those values
  // read-only and takes no part in validation or the override dots. Reps are
  // edited in the care plan (clinic/company) or the schedule row (patient).
  const repsFromPlan =
    props.repsByWeek && typeof props.repsByWeek === 'object' ? props.repsByWeek : null
  const repsSource = props.repsSource || 'the care plan'
  const selectedWeek = typeof props.selectedWeek === 'number' ? props.selectedWeek : null
  const exerciseName = props.exerciseName || ''

  // Cells the clinician has deliberately cleared. Tracked as local state and
  // NOT written into the persisted overrides, so a per-week row never carries a
  // null angle downstream (server / patient app). Its only jobs: keep an
  // emptied InputNumber visually empty — instead of the controlled value
  // snapping back to the field's default — and mark that cell as blocking
  // for Save. Keyed `${week}:${field}`; cleared once a valid number is typed.
  const [emptyCells, setEmptyCells] = useState({})
  const cellKey = (week, field) => `${week}:${field}`
  const isCellEmpty = (week, field) => !!emptyCells[cellKey(week, field)]

  // Resolve a field's display value: override wins, else master, else undefined
  const v = (field) =>
    overrides[field] !== undefined && overrides[field] !== null
      ? overrides[field]
      : masterConfig[field]

  // Mark a field as overridden (different from master) for visual emphasis
  const isOverridden = (field) =>
    overrides[field] !== undefined && overrides[field] !== null && overrides[field] !== masterConfig[field]

  // Push a field update into the overrides object passed to onChange
  const setField = (field, value) => {
    if (!props.onChange) return
    const next = { ...overrides }
    if (value === undefined || value === null || value === '') {
      delete next[field]
    } else {
      next[field] = value
    }
    props.onChange(next)
  }

  // REP MILESTONE — per-week editor helpers.
  // Strip any legacy {week, milestone} rows so the old band string can't
  // accidentally surface in the new form. Migration is by attrition: any row
  // without a numeric field (angle OR reps) is treated as not-present.
  const repMilestoneByWeek = Array.isArray(overrides.rep_milestone_by_week)
    ? overrides.rep_milestone_by_week.filter((r) =>
        r && WEEK_ROW_FIELDS.some((f) => r[f] !== undefined && r[f] !== null)
      )
    : []

  // Shared ctx for the per-week resolver. The modal builds its own ctx with
  // the same shape so the table values and the preview bands stay in sync.
  // tierDefaults is the global-baseline per-tier table from the master config;
  // tieredDefault reads it instead of a hardcoded table.
  // clinicRepMilestoneByWeek = the clinic's per-week rows, resolved server-side
  // onto masterConfig (rep_milestone_by_week). They form the baseline this
  // patient inherits where no per-patient row exists, so interpolatedDefault
  // (the dot / clearing baseline) layers them above the tiered/master default.
  const weekCtx = {
    repMilestoneByWeek,
    resolved: v,
    exerciseName,
    tierDefaults: masterConfig.tierDefaults,
    clinicRepMilestoneByWeek: masterConfig.rep_milestone_by_week,
    overrides,
  }
  const interpolatedDefault = (week, field) => clinicOrTieredDefault(week, field, weekCtx)
  const weekCellValue = (week, field) => resolveWeekCell(week, field, weekCtx)

  // Back-compat alias — internal monotonicity check still calls weekAngle().
  const weekAngle = (week, field) => weekCellValue(week, field)

  // Cell is "overridden" (gets the blue dot) only when its stored value
  // differs from the auto-stepped default for that week. Matching the
  // default is treated as "no override" so the indicator stays clean
  // until the clinician actively changes the value.
  const isCellOverridden = (week, field) => {
    if (field === 'reps' && repsFromPlan) return false
    const entry = repMilestoneByWeek.find((r) => r && r.week === week)
    return !!(
      entry &&
      entry[field] !== undefined &&
      entry[field] !== null &&
      entry[field] !== interpolatedDefault(week, field)
    )
  }

  const isWeekOverridden = (week) =>
    WEEK_ROW_FIELDS.some((f) => isCellOverridden(week, f))

  const setRepMilestoneCell = (week, field, value) => {
    if (!props.onChange) return
    const existing = repMilestoneByWeek.find((r) => r && r.week === week)
    const draft = { ...(existing || { week }) }
    // Cleared / matching the auto-stepped default → drop the cell so it
    // falls back through the interpolation. Otherwise persist explicitly.
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      value === interpolatedDefault(week, field)
    ) {
      delete draft[field]
    } else {
      draft[field] = value
    }
    const hasAny = WEEK_ROW_FIELDS.some(
      (f) => draft[f] !== undefined && draft[f] !== null
    )
    const others = repMilestoneByWeek.filter((r) => r && r.week !== week)
    const nextArr = hasAny
      ? [...others, draft].sort((a, b) => a.week - b.week)
      : others
    const next = { ...overrides }
    if (nextArr.length === 0) {
      delete next.rep_milestone_by_week
    } else {
      next.rep_milestone_by_week = nextArr
    }
    props.onChange(next)
  }

  // Cell edit entry point. A cleared value is remembered as deliberately-empty
  // (see emptyCells) rather than deleted-and-defaulted, so the field stays
  // empty and blocks Save. Any valid number clears the empty flag and flows
  // through the normal override path (setRepMilestoneCell). Note: on clear we
  // still call setRepMilestoneCell(null) to drop any stored override for the
  // cell; the empty display is driven by emptyCells, so it won't snap back.
  const handleCellChange = (week, field, value) => {
    const key = cellKey(week, field)
    const cleared = value === undefined || value === null || value === ''
    setEmptyCells((prev) => {
      if (cleared) return prev[key] ? prev : { ...prev, [key]: true }
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
    setRepMilestoneCell(week, field, value)
  }

  // Per-week monotonicity. The master limits (extension_limit, flexion_limit)
  // stay outer bounds; each row's 4 resolved angles must keep the chain
  // ext_limit ≤ ext_high ≤ ext_low ≤ flex_low ≤ flex_high ≤ flex_limit.
  const weekMonotonicError = (week) => {
    const seq = [
      { label: 'extension_limit', value: v('extension_limit') },
      { label: 'extension_high',  value: weekAngle(week, 'extension_high') },
      { label: 'extension_low',   value: weekAngle(week, 'extension_low') },
      { label: 'flexion_low',     value: weekAngle(week, 'flexion_low') },
      { label: 'flexion_high',    value: weekAngle(week, 'flexion_high') },
      { label: 'flexion_limit',   value: v('flexion_limit') },
    ]
    for (let i = 1; i < seq.length; i++) {
      const a = seq[i - 1]
      const b = seq[i]
      if (a.value != null && b.value != null && !(a.value <= b.value)) {
        const aLabel = ANGLE_FIELD_LABELS[a.label] || a.label
        const bLabel = ANGLE_FIELD_LABELS[b.label] || b.label
        return `${aLabel} (${a.value}°) must be ≤ ${bLabel} (${b.value}°)`
      }
    }
    return null
  }

  const fieldLabel = (text, field) => (
    <div
      style={
        isOverridden(field)
          ? { ...fieldLabelStyle, color: '#1890ff' }
          : fieldLabelStyle
      }
    >
      {text}
      {isOverridden(field) ? <span style={overriddenDot} /> : null}
    </div>
  )
  const inputFull = { width: '100%' }

  // Resolve bandsRequired: an empty-array override means "not set" — fall
  // through to the master defaults (the band checkboxes were removed from
  // this form 2026-10-19; a stored override still counts). This prevents stale `bandsRequired: []` entries on legacy schedule
  // docs from shadowing the master (e.g. Sitting Lift's ['ext', 'flex']).
  const overrideBands = overrides.bandsRequired
  const bandsRequired =
    Array.isArray(overrideBands) && overrideBands.length > 0
      ? overrideBands
      : (masterConfig.bandsRequired || [])

  // Monotonicity check across all 6 angles using resolved values.
  // Mirrors the server-side validator in physio_server/data/models/exercise.js
  // Canonical chain: ext_limit ≤ ext_high ≤ ext_low ≤ flex_low ≤ flex_high ≤ flex_limit.
  const angleSeq = [
    { label: 'extension_limit', value: v('extension_limit') },
    { label: 'extension_high',  value: v('extension_high') },
    { label: 'extension_low',   value: v('extension_low') },
    { label: 'flexion_low',     value: v('flexion_low') },
    { label: 'flexion_high',    value: v('flexion_high') },
    { label: 'flexion_limit',   value: v('flexion_limit') },
  ]
  let monotonicError = null
  for (let i = 1; i < angleSeq.length; i++) {
    const a = angleSeq[i - 1]
    const b = angleSeq[i]
    if (a.value != null && b.value != null && !(a.value <= b.value)) {
      const aLabel = ANGLE_FIELD_LABELS[a.label] || a.label
      const bLabel = ANGLE_FIELD_LABELS[b.label] || b.label
      monotonicError = `${aLabel} (${a.value}°) must be ≤ ${bLabel} (${b.value}°)`
      break
    }
  }

  // Aggregate every blocking condition so the modal can disable Save:
  //   1. any monotonicity violation (top-level resolved chain OR a per-week row)
  //   2. any required, band-relevant cell left empty
  // Empty is scoped to the cells that actually gate the exercise — reps always,
  // flexion angles only when the flex band is required, extension angles only
  // when the ext band is required — so a legitimately one-sided config (e.g. an
  // ext-only exercise with null flexion angles) isn't flagged as invalid.
  const requiredWeekFields = repsFromPlan ? [] : ['reps']
  if (bandsRequired.includes('flex')) requiredWeekFields.push('flexion_low', 'flexion_high')
  if (bandsRequired.includes('ext')) requiredWeekFields.push('extension_low', 'extension_high')

  // NOTE: use array methods, not for…of — a for…of/for…in before a hook
  // trips eslint-plugin-react-hooks into a false "called conditionally" error.
  const allWeeks = [-1, ...Array.from({ length: numWeeks }, (_, i) => i)]
  const hasWeekMonotonicError = allWeeks.some((week) => !!weekMonotonicError(week))
  const emptyFieldErrors = []
  allWeeks.forEach((week) => {
    WEEK_ROW_FIELDS.forEach((f) => {
      if (f === 'reps' && repsFromPlan) return // shown from the care plan, never blocking
      // Blocking-empty when the clinician actively cleared the cell
      // (userEmptied), or when a band-relevant required cell resolves to no
      // value at all. A non-required side (e.g. flexion on an ext-only
      // exercise) is only flagged if actively cleared — never when it's
      // legitimately absent from the master config.
      const userEmptied = isCellEmpty(week, f)
      const resolvedEmpty =
        requiredWeekFields.includes(f) && weekCellValue(week, f) == null
      if (userEmptied || resolvedEmpty) {
        const label = ANGLE_FIELD_LABELS[f] || (f === 'reps' ? 'Reps' : f)
        emptyFieldErrors.push(`${week === -1 ? 'Pre-Op' : `Week ${week}`}: ${label}`)
      }
    })
  })

  const isValid = !monotonicError && !hasWeekMonotonicError && emptyFieldErrors.length === 0

  // Report validity up so TracPatchConfigModal can block Save on invalid/empty
  // input. Runs after render (never during) to avoid setting parent state mid-render.
  useEffect(() => {
    if (props.onValidityChange) props.onValidityChange(isValid)
  }, [isValid, props.onValidityChange])

  return (
    <div>
      {monotonicError && (
        <div style={{ color: '#cf1322', fontSize: 12, marginBottom: 10, padding: 10, background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 8 }}>
          ⚠ Angles out of order: {monotonicError}. The save will be rejected by the server.
        </div>
      )}

      {emptyFieldErrors.length > 0 && (
        <div style={{ color: '#cf1322', fontSize: 12, marginBottom: 10, padding: 10, background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 8 }}>
          ⚠ Required values are empty — fill them in before saving: {emptyFieldErrors.join(', ')}
        </div>
      )}

      {/* TRACPATCH OVERRIDES (posture, mode, bands required) removed
          2026-10-19 at the client's request: these are set per exercise and
          are not configurable here. The stored values still apply; the
          REP MILESTONE table below still keys its required columns off the
          resolved bandsRequired. */}

      {/* EXTENSION BAND / FLEXION BAND sections removed — Low/High angles are
          now configured per-week in REP MILESTONE (and fall back to the
          master exercise's tracpatchConfig), and the hard limits are no
          longer patient-editable: they come straight from masterConfig. */}

      {/* TARGETS */}
      <div style={cardStyle}>
        <div style={sectionHeaderRow}>
          <span style={sectionTitle}>TARGETS</span>
        </div>
        <Row gutter={16}>
          <Col span={8}>
            <div style={fieldLabelStyle}>Sets</div>
            <InputNumber
              value={props.sets != null ? props.sets : masterConfig.requiredSets}
              disabled={disabled || !props.onSetsChange}
              onChange={(val) => props.onSetsChange && props.onSetsChange(val)}
              min={0}
              style={inputFull}
            />
          </Col>
          <Col span={8}>
            {fieldLabel('Hold (s)', 'requiredHoldSeconds')}
            <InputNumber
              value={v('requiredHoldSeconds')}
              disabled={disabled}
              onChange={(val) => setField('requiredHoldSeconds', val)}
              min={0}
              style={inputFull}
            />
          </Col>
          <Col span={8}>
            {fieldLabel('Rest angle (°)', 'restAngle')}
            <InputNumber value={v('restAngle')} disabled={disabled} onChange={(val) => setField('restAngle', val)} style={inputFull} />
          </Col>
        </Row>
      </div>

      {/* REP MILESTONE — per-week angle table, one row per post-op week */}
      <div style={cardStyle}>
        <div style={sectionHeaderRow}>
          <span style={sectionTitle}>REP MILESTONE</span>
        </div>
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            overflow: 'hidden',
            background: '#fff',
          }}
        >
          <Row
            gutter={8}
            style={{
              background: '#f8fafc',
              borderBottom: '1px solid #e5e7eb',
              padding: '10px 9px 10px 12px',
              alignItems: 'center',
            }}
          >
            <Col span={3}><div style={tableHeaderStyle}>Week</div></Col>
            {/* Column-to-field mapping (canonical chain: ext_high ≤
                ext_low ≤ flex_low ≤ flex_high):
                  Higher Flexion  → flexion_high   (strict flex gate)
                  Lower Flexion   → flexion_low    (lenient flex gate)
                  Lower Extension → extension_low  (lenient ext gate, larger angle)
                  Higher Extension → extension_high (strict ext gate, smaller angle / more extended)
                Week column at span=3 keeps "Week 10..13" + the override
                dot from spilling into the first input. Totals: 3+5+4+5+5+2 = 24. */}
            <Col span={5}><div style={tableHeaderStyle}>Higher Flexion</div></Col>
            <Col span={4}><div style={tableHeaderStyle}>Lower Flexion</div></Col>
            <Col span={5}><div style={tableHeaderStyle}>Lower Extension</div></Col>
            <Col span={5}><div style={tableHeaderStyle}>Higher Extension</div></Col>
            <Col span={2}>
              <div
                style={tableHeaderStyle}
                title={repsFromPlan ? `Reps come from ${repsSource}` : undefined}
              >
                Reps{repsFromPlan ? ' *' : ''}
              </div>
            </Col>
          </Row>
          {/* Render Pre-Op (week = -1) first, then weeks 0..numWeeks-1.
              The Pre-Op row stores its angles + reps under week=-1 in
              rep_milestone_by_week and drives the patient session whenever
              today is before surgery date. */}
          {[-1, ...Array.from({ length: numWeeks }, (_, i) => i)].map((week, idx, arr) => {
            const overridden = isWeekOverridden(week)
            const rowError = weekMonotonicError(week)
            const isSelected = selectedWeek === week
            const isLast = idx === arr.length - 1
            const cell = (field) =>
              field === 'reps' && repsFromPlan ? (
                <InputNumber
                  value={repsFromPlan[week] != null ? repsFromPlan[week] : null}
                  disabled
                  placeholder='—'
                  title={
                    repsFromPlan[week] != null
                      ? `From ${repsSource}`
                      : `No ${repsSource} window covers this week`
                  }
                  style={inputFull}
                />
              ) : (
              <InputNumber
                value={isCellEmpty(week, field) ? null : weekCellValue(week, field)}
                disabled={disabled}
                min={field === 'reps' ? 0 : undefined}
                onChange={(val) => {
                  handleCellChange(week, field, val)
                  if (props.onSelectWeek) props.onSelectWeek(week)
                }}
                onFocus={() => props.onSelectWeek && props.onSelectWeek(week)}
                style={
                  isCellEmpty(week, field)
                    ? { ...inputFull, borderColor: '#ff4d4f' }
                    : isCellOverridden(week, field)
                    ? { ...inputFull, borderColor: '#1890ff' }
                    : inputFull
                }
              />
              )
            return (
              <div
                key={week}
                onClick={() => props.onSelectWeek && props.onSelectWeek(week)}
                style={{
                  cursor: props.onSelectWeek ? 'pointer' : 'default',
                  background: isSelected ? '#f0fdf4' : '#fff',
                  borderLeft: isSelected ? '3px solid #22c55e' : '3px solid transparent',
                  borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
                  padding: '10px 9px 10px 12px',
                }}
              >
                <Row gutter={8} style={{ alignItems: 'center' }}>
                  <Col span={3}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: isSelected ? '#15803d'
                          : overridden ? '#1890ff'
                          : '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {/* Week -1 is the dedicated Pre-Op row applied for
                          all pre-surgery dates. Weeks 0..13 are the
                          post-op recovery weeks. */}
                      {week === -1 ? 'Pre-Op' : `Week ${week}`}
                      {overridden ? <span style={overriddenDot} /> : null}
                    </div>
                  </Col>
                  <Col span={5}>{cell('flexion_high')}</Col>
                  <Col span={4}>{cell('flexion_low')}</Col>
                  <Col span={5}>{cell('extension_low')}</Col>
                  <Col span={5}>{cell('extension_high')}</Col>
                  <Col span={2}>{cell('reps')}</Col>
                </Row>
                {rowError && (
                  <div style={{ color: '#cf1322', fontSize: 11, marginTop: 6 }}>
                    ⚠ {rowError}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
          Each angle cell defaults to this exercise's master value. Edit a cell
          to override that week. Rep gate hits at the row's Higher Flexion
          (flex side) and Higher Extension (ext side, the smaller / more
          extended angle).{' '}
          {repsFromPlan
            ? `* Reps come from ${repsSource} and are shown here read-only; a dash means no window of ${repsSource} covers that week.`
            : "The row's Reps cell sets how many reps complete a set that week."}{' '}
          Click a row to preview that week in the patient view.
        </div>
      </div>

      {Object.keys(overrides).length > 0 && (
        <div style={{ marginTop: 4, marginBottom: 8 }}>
          <a
            style={{ fontSize: 12, color: '#64748b' }}
            onClick={() => {
              setEmptyCells({})
              if (props.onChange) props.onChange({})
            }}
          >
            ↺ Reset all to defaults
          </a>
        </div>
      )}
    </div>
  )
}

TracPatchOverrideForm.propTypes = {
  masterConfig: PropTypes.object,
  overrides: PropTypes.object,
  onChange: PropTypes.func,
  // Called with a boolean whenever the form's validity changes, so the modal
  // can enable/disable Save. true = every value is present and in order.
  onValidityChange: PropTypes.func,
  edit: PropTypes.bool,
  numWeeks: PropTypes.number,
  selectedWeek: PropTypes.number,
  onSelectWeek: PropTypes.func,
  // Sets lives on the schedule entry (not in overrides), so the modal owns it
  // and passes value + setter down for the TARGETS card.
  sets: PropTypes.number,
  onSetsChange: PropTypes.func,
  // Exercise name drives the tiered-default branch (e.g. Sitting Lift /
  // Sit to Stand have bespoke per-tier targets).
  exerciseName: PropTypes.string,
  // Reps per week from the care plan ({ [week]: reps | null }). When given,
  // the Reps column is read-only and shows these; `repsSource` names where
  // they come from in the hint text.
  repsByWeek: PropTypes.object,
  repsSource: PropTypes.string,
}

export default TracPatchOverrideForm
